import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { ArrowLeft, ArrowRight, Search, CheckSquare, Loader2, Clock, MapPin, X } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { authFetch } from "@/utils/authFetch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Step2AddressCustomer({ formData, updateForm, onNext, onPrev }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [recentAddresses, setRecentAddresses] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [autocompleteLoading, setAutocompleteLoading] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  // Load recent addresses on mount
  useEffect(() => {
    authFetch(`${API}/user/recent-addresses`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : { addresses: [] })
      .then((d) => setRecentAddresses(d.addresses || []))
      .catch(() => {});
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const isPostcode = (q) => /^[A-Z]{1,2}\d/.test(q.trim().toUpperCase());

  // Combined autocomplete: postcodes + places
  const handleInput = (value) => {
    const upper = value.toUpperCase();
    setSearchQuery(upper);
    setSearched(false);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (upper.length >= 2) {
      setAutocompleteLoading(true);
      debounceRef.current = setTimeout(async () => {
        try {
          const results = [];
          if (isPostcode(upper)) {
            const { data } = await axios.get(`${API}/addresses/autocomplete`, {
              params: { query: upper }, withCredentials: true,
            });
            (data.suggestions || []).forEach((pc) => results.push({ type: "postcode", label: pc, postcode: pc }));
          }
          if (upper.length >= 3) {
            const { data } = await axios.get(`${API}/addresses/places`, {
              params: { query: upper }, withCredentials: true,
            });
            (data.places || []).forEach((p) => results.push({ type: "place", label: p.name, postcode: p.postcode, county: p.county }));
          }
          setSuggestions(results);
          setShowDropdown(results.length > 0);
        } catch {
          setSuggestions([]);
        } finally {
          setAutocompleteLoading(false);
        }
      }, 300);
    } else {
      setSuggestions([]);
      setShowDropdown(false);
      setAutocompleteLoading(false);
    }
  };

  const selectSuggestion = (item) => {
    setShowDropdown(false);
    setSuggestions([]);
    if (item.postcode) {
      setSearchQuery(item.postcode);
      lookupPostcode(item.postcode);
    }
  };

  const selectRecentAddress = (addr) => {
    if (addr.postcode) {
      setSearchQuery(addr.postcode);
      lookupPostcode(addr.postcode);
    }
    // Pre-fill premise/street from the address string
    const parts = (addr.address || "").split(",").map((s) => s.trim());
    if (parts.length >= 1) updateForm({ premise: parts[0] });
    if (addr.client_name) updateForm({ homeowner_name: addr.client_name });
    if (addr.client_email) updateForm({ homeowner_email: addr.client_email });
    toast.success("Recent address loaded");
  };

  const handleSearch = () => {
    if (!searchQuery.trim()) { toast.error("Please enter a postcode or address to search"); return; }
    lookupPostcode(searchQuery);
  };

  const lookupPostcode = async (postcode) => {
    setSearching(true);
    setSearched(false);
    try {
      const { data } = await axios.get(`${API}/addresses/search`, {
        params: { postcode }, withCredentials: true,
      });
      setSearched(true);
      if (data.error) {
        toast.error(data.error);
      } else if (data.addresses?.length >= 1) {
        selectAddress(data.addresses[0]);
        toast.success("Postcode found — please enter the house number / street name below");
      } else {
        toast.error("No addresses found for this postcode");
      }
    } catch {
      toast.error("Failed to search addresses");
    } finally {
      setSearching(false);
    }
  };

  const selectAddress = (addr) => {
    updateForm({
      premise: addr.premise || formData.premise || "",
      street: addr.street || formData.street || "",
      locality: addr.locality || "",
      town: addr.town || "",
      county: addr.county || "",
      postcode: addr.postcode || "",
      local_authority: addr.local_authority || "",
      local_authority_email: addr.local_authority_email || "",
      authority_confirmed: false,
    });
  };

  const handleNext = () => {
    if (!formData.postcode.trim()) { toast.error("Please search and select an address"); return; }
    if (!formData.local_authority.trim()) { toast.error("Local authority is required"); return; }
    if (!formData.authority_confirmed) { toast.error("Please confirm the local authority by checking the box"); return; }
    if (!formData.homeowner_name.trim()) { toast.error("Please enter the homeowner name"); return; }
    if (!formData.homeowner_email.trim()) { toast.error("Please enter the homeowner email"); return; }
    if (!formData.homeowner_phone.trim()) { toast.error("Please enter the homeowner phone number"); return; }
    onNext();
  };

  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 11);
    if (digits.length <= 4) return digits;
    return `(${digits.slice(0, 4)}) ${digits.slice(4)}`;
  };

  const hasPostcodeData = formData.postcode && formData.town;

  return (
    <div className="form-container max-w-3xl mx-auto" data-testid="step-2-form">
      <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>
        Installation Address and Customer
      </h2>
      <div className="space-y-6">
        {/* Country & Region */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-secondary)' }}>Country</label>
            <Select value={formData.country} onValueChange={(v) => updateForm({ country: v })}>
              <SelectTrigger data-testid="country-select" className="brutalist-input h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-xl border" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-main)' }}>
                <SelectItem value="United Kingdom">United Kingdom</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-secondary)' }}>Region</label>
            <Select value={formData.region} onValueChange={(v) => updateForm({ region: v })}>
              <SelectTrigger data-testid="region-select" className="brutalist-input h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-xl border" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-main)' }}>
                <SelectItem value="England and Wales">England and Wales</SelectItem>
                <SelectItem value="Scotland">Scotland</SelectItem>
                <SelectItem value="Northern Ireland">Northern Ireland</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* New Build Checkbox */}
        <div className="flex items-center gap-3">
          <Checkbox
            data-testid="new-build-checkbox"
            checked={formData.is_new_build}
            onCheckedChange={(checked) => updateForm({ is_new_build: !!checked })}
            className="w-5 h-5 rounded data-[state=checked]:bg-[#D4A537] data-[state=checked]:border-[#D4A537]"
          />
          <label className="text-sm font-medium" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: 'var(--text-primary)' }}>
            Please tick if New Build
          </label>
        </div>

        {/* Recent Addresses */}
        {recentAddresses.length > 0 && !hasPostcodeData && (
          <div data-testid="recent-addresses">
            <label className="block text-xs font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-secondary)' }}>
              <Clock className="w-3.5 h-3.5 inline mr-1" /> Recent Addresses
            </label>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {recentAddresses.slice(0, 6).map((addr, i) => (
                <button key={i} data-testid={`recent-addr-${i}`} onClick={() => selectRecentAddress(addr)}
                  className="flex-shrink-0 rounded-xl px-3 py-2 text-left text-xs border transition-all hover:shadow-sm"
                  style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-main)', color: 'var(--text-primary)', minWidth: '160px', maxWidth: '220px' }}>
                  <p className="font-semibold truncate">{addr.address}</p>
                  <p className="truncate" style={{ color: 'var(--text-secondary)', fontFamily: 'IBM Plex Mono, monospace', fontSize: '10px' }}>{addr.postcode}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Address Search */}
        <div ref={wrapperRef} className="relative">
          <label className="block text-xs font-semibold mb-1.5" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-secondary)' }}>
            <MapPin className="w-3.5 h-3.5 inline mr-1" /> Search by Postcode or Place Name
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <input
                data-testid="postcode-search-input"
                type="text"
                value={searchQuery}
                onChange={(e) => handleInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { setShowDropdown(false); handleSearch(); } }}
                onFocus={() => { if (suggestions.length > 0) setShowDropdown(true); }}
                className="brutalist-input pl-10"
                placeholder="e.g. SW1A 1AA or Doncaster"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-muted)' }} />
              {autocompleteLoading && (
                <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
              )}

              {/* Combined Suggestions Dropdown */}
              {showDropdown && suggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 rounded-xl overflow-hidden max-h-56 overflow-y-auto" style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 8px 30px rgba(0,0,0,0.12)', border: '1px solid var(--border-main)' }} data-testid="postcode-suggestions">
                  {suggestions.map((item, i) => (
                    <button
                      key={i}
                      data-testid={`suggestion-${i}`}
                      onClick={() => selectSuggestion(item)}
                      className="w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 transition-colors hover:bg-[#E8F7FC]"
                      style={{ borderBottom: '1px solid var(--border-light)' }}
                    >
                      <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: item.type === 'postcode' ? '#E8F7FC' : '#EAFBF0' }}>
                        {item.type === 'postcode'
                          ? <span className="text-[9px] font-bold text-[#00A3E0]">PC</span>
                          : <MapPin className="w-3 h-3 text-[#22C55E]" />
                        }
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold" style={{ fontFamily: item.type === 'postcode' ? 'IBM Plex Mono, monospace' : 'inherit', color: 'var(--text-primary)' }}>
                          {item.label}
                        </p>
                        {item.type === 'place' && item.county && (
                          <p className="text-[10px] truncate" style={{ color: 'var(--text-secondary)' }}>{item.county}{item.postcode ? ` · ${item.postcode}` : ''}</p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              data-testid="search-address-button"
              onClick={handleSearch}
              disabled={searching}
              className="brutalist-btn flex items-center gap-2 flex-shrink-0"
              style={{ backgroundColor: '#00A3E0' }}
            >
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {searching ? "..." : "Search"}
            </button>
          </div>
          <p className="text-[10px] mt-1.5" style={{ color: 'var(--text-muted)' }}>
            Powered by Postcodes.io — Royal Mail postcode data
          </p>
        </div>

        {/* Address Fields — prominently shown after postcode found */}
        {hasPostcodeData && (
          <div className="rounded-xl p-4 border animate-fade-in-up" style={{ backgroundColor: 'var(--row-alt)', borderColor: 'var(--border-main)' }} data-testid="address-fields-found">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: '#22C55E' }}>
                <MapPin className="w-3.5 h-3.5 inline mr-1" /> Postcode found — complete the address below
              </p>
              <span className="text-xs font-bold" style={{ fontFamily: 'IBM Plex Mono, monospace', color: 'var(--text-primary)' }}>{formData.postcode}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>House No. / Name *</label>
                <input data-testid="premise-input" type="text" value={formData.premise} onChange={(e) => updateForm({ premise: e.target.value })} className="brutalist-input" placeholder="e.g. 42 or Rose Cottage" autoFocus />
              </div>
              <div>
                <label className="block text-[10px] font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>Street *</label>
                <input data-testid="street-input" type="text" value={formData.street} onChange={(e) => updateForm({ street: e.target.value })} className="brutalist-input" placeholder="e.g. Oak Lane" />
              </div>
              <div>
                <label className="block text-[10px] font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>Locality</label>
                <input data-testid="locality-input" type="text" value={formData.locality} onChange={(e) => updateForm({ locality: e.target.value })} className="brutalist-input" />
              </div>
              <div>
                <label className="block text-[10px] font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>Town</label>
                <input data-testid="town-input" type="text" value={formData.town} onChange={(e) => updateForm({ town: e.target.value })} className="brutalist-input" />
              </div>
              <div>
                <label className="block text-[10px] font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>County</label>
                <input data-testid="county-input" type="text" value={formData.county} onChange={(e) => updateForm({ county: e.target.value })} className="brutalist-input" />
              </div>
              <div>
                <label className="block text-[10px] font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>Postcode</label>
                <input data-testid="postcode-input" type="text" value={formData.postcode} readOnly className="brutalist-input" style={{ backgroundColor: 'var(--border-light)' }} />
              </div>
            </div>
          </div>
        )}

        {/* Address Fields when no postcode yet */}
        {!hasPostcodeData && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { id: "premise", label: "Premise", ph: "House number or name", field: "premise" },
              { id: "street", label: "Street", ph: "Street name", field: "street" },
              { id: "locality", label: "Locality", field: "locality" },
              { id: "town", label: "Town", field: "town" },
              { id: "county", label: "County", field: "county" },
              { id: "postcode", label: "Postcode", field: "postcode", readOnly: true },
            ].map((f) => (
              <div key={f.id}>
                <label className="block text-xs font-semibold mb-1.5" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-secondary)' }}>{f.label}</label>
                <input data-testid={`${f.id}-input`} type="text" value={formData[f.field]} onChange={(e) => !f.readOnly && updateForm({ [f.field]: e.target.value })} className="brutalist-input" placeholder={f.ph || ""} readOnly={f.readOnly} />
              </div>
            ))}
          </div>
        )}

        {/* Local Authority */}
        <div>
          <label className="block text-xs font-semibold mb-1.5" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-secondary)' }}>Local Authority</label>
          <input data-testid="local-authority-input" type="text" value={formData.local_authority} readOnly className="brutalist-input" style={{ backgroundColor: 'var(--border-light)' }} />
          {formData.local_authority && (
            <div className="mt-3 flex items-center gap-3 rounded-xl border p-4" style={{ backgroundColor: 'var(--row-alt)', borderColor: 'var(--border-main)' }} data-testid="authority-confirmation">
              <Checkbox
                data-testid="authority-confirm-checkbox"
                checked={formData.authority_confirmed}
                onCheckedChange={(checked) => updateForm({ authority_confirmed: !!checked })}
                className="w-5 h-5 rounded data-[state=checked]:bg-[#D4A537] data-[state=checked]:border-[#D4A537]"
              />
              <div className="flex-1">
                <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                  <CheckSquare className="w-4 h-4 inline mr-1" />
                  {formData.local_authority}
                </p>
                <p className="text-xs mt-0.5" style={{ fontFamily: 'IBM Plex Mono, monospace', color: 'var(--text-secondary)' }}>{formData.local_authority_email}</p>
              </div>
            </div>
          )}
        </div>

        {/* Homeowner Details */}
        <div className="border-t pt-6" style={{ borderColor: 'var(--border-main)' }}>
          <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>Homeowner Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold mb-1.5" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-secondary)' }}>Homeowner Email</label>
              <input data-testid="homeowner-email-input" type="email" value={formData.homeowner_email} onChange={(e) => updateForm({ homeowner_email: e.target.value })} className="brutalist-input" placeholder="homeowner@email.com" />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-secondary)' }}>Homeowner Name</label>
              <input data-testid="homeowner-name-input" type="text" value={formData.homeowner_name} onChange={(e) => updateForm({ homeowner_name: e.target.value })} className="brutalist-input" placeholder="Full name" />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-secondary)' }}>Homeowner Phone</label>
              <input data-testid="homeowner-phone-input" type="tel" value={formData.homeowner_phone} onChange={(e) => updateForm({ homeowner_phone: formatPhone(e.target.value) })} className="brutalist-input" placeholder="(0###) ######" />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 flex justify-between">
        <button data-testid="step-2-prev-button" onClick={onPrev} className="brutalist-btn-outline flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> Previous
        </button>
        <button data-testid="step-2-next-button" onClick={handleNext} className="brutalist-btn flex items-center gap-2">
          Next <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
